import React from 'react';
import {
  Box,
  CircularProgress,
  styled,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import moment from 'moment';
import { useState } from 'react';

const DAY_SIZE = 36 // from @mui/x-date-pickers/internals/constants/dimensions
const DAY_MARGIN = 2

// styling taken from MUI DayCalendar https://github.com/mui/mui-x/blob/97220aaf2964a9dcd08d6a2a263f4f1610d71a75/packages/x-date-pickers/src/DateCalendar/DayCalendar.tsx

const PickersBoxHeader = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const PickersBoxColumnLabel = styled(Typography)(({ theme }) => ({
  width: 36,
  height: 40,
  margin: '0 2px',
  textAlign: 'center',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: theme.palette.text.disabled,
}));

const PickersBoxRowContainer = styled('div')({ overflow: 'hidden' });

const PickersBoxRow = styled('div')({
  margin: `${DAY_MARGIN}px 0`,
  display: 'flex',
  justifyContent: 'center',
});

const PickersBoxRowNumber = styled(Typography)(({ theme }) => ({
  ...theme.typography.caption,
  width: DAY_SIZE,
  height: DAY_SIZE,
  padding: 0,
  margin: `0 ${DAY_MARGIN}px`,
  color: theme.palette.text.disabled,
  fontSize: '0.75rem',
  alignItems: 'center',
  justifyContent: 'center',
  display: 'inline-flex',
}));

const LocationPickers = styled('button', {
  shouldForwardProp: (prop) => !['occupied', 'selected'].includes(prop),
})(({ theme, occupied, selected }) => ({
  ...theme.typography.caption,
  width: DAY_SIZE,
  height: DAY_SIZE,
  padding: 0,
  margin: `0 ${DAY_MARGIN}px`,
  border: 0,
  borderRadius: '50%',
  color: theme.palette.text.primary,
  backgroundColor: 'transparent',
  fontFamily: 'inherit',
  alignItems: 'center',
  justifyContent: 'center',
  display: 'inline-flex',
  cursor: 'pointer',
  ...(occupied && {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.text.disabled,
    textDecoration: 'line-through',
  }),
  ...(selected && {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textDecoration: 'none',
  }),
  '&:hover:not(:disabled)': {
    backgroundColor: selected
      ? theme.palette.primary.dark
      : occupied
        ? theme.palette.action.disabledBackground
        : theme.palette.action.hover,
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

export default function LocationPositionViewer({numRows, numCols, occupiedPositions, positionSamples, selectedPositions, rowsLettered, onSelect}) {
  const isMobile = useMediaQuery('(hover: none)')
  const [openSamplePosition, setOpenSamplePosition] = useState()
  const unravelIdx = (row, col) => numCols ? row * numCols + col : col
  if (occupiedPositions === undefined)
    return <CircularProgress />
  return (
    <div role="grid">
      <PickersBoxHeader role="row">
        <PickersBoxColumnLabel /> {/* gap for row labels */}
          {[...Array(numCols).keys()].map(col => (
            <PickersBoxColumnLabel
              key={col}
              variant="caption"
              role="columnheader"
            >
              {col + 1}
            </PickersBoxColumnLabel>
          ))}
      </PickersBoxHeader>
      <PickersBoxRowContainer role="rowgroup">
        {[...Array(numRows).keys()].map((row, index) => (
          <PickersBoxRow
            key={row}
            role="row"
            aria-rowindex={index + 1}
          >
            <PickersBoxRowNumber
              role="rowheader"
              scope="row"
            >
              {rowsLettered ? "ABCDEFGHIJKLMNOPQRSTUVWXYZ"[row] : row + 1}
            </PickersBoxRowNumber>
            {[...Array(numCols).keys()].map(col => {
              const position = unravelIdx(row, col) + 1
              const occupied = occupiedPositions[unravelIdx(row, col)] || false
              const sample = positionSamples?.[position]
              const selected = selectedPositions?.includes(position)
              return (
                <Tooltip
                  key={col}
                  arrow
                  open={isMobile ? openSamplePosition === position : undefined}
                  onClose={() => setOpenSamplePosition()}
                  title={sample ? (
                    <Box sx={{maxWidth: 220, p: 0.25}}>
                      <Typography variant="subtitle2" color="inherit" sx={{display: 'block'}}>
                        {sample.name}
                      </Typography>
                      <Typography variant="caption" color="inherit" sx={{display: 'block'}}>
                        {sample.type}
                      </Typography>
                      {sample.description &&
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          noWrap
                          sx={{maxWidth: 210, overflow: 'hidden', textOverflow: 'ellipsis', display: 'block'}}
                        >
                          {sample.description}
                        </Typography>
                      }
                      {sample.date &&
                      <Typography variant="caption" color="textSecondary" sx={{display: 'block'}}>
                        {moment(sample.date).format("M/D/YYYY")}
                      </Typography>
                      }
                    </Box>
                  ) : ''}
                >
                  <LocationPickers
                    role="gridcell"
                    aria-selected={selected}
                    selected={selected}
                    occupied={occupied}
                    data-occupied={occupied || undefined}
                    type="button"
                    onClick={event => {
                      if (isMobile && sample) {
                        if (openSamplePosition !== position) {
                          setOpenSamplePosition(position)
                          return
                        }
                        setOpenSamplePosition()
                      }
                      onSelect?.(selected ? null : position, event)
                    }}
                  >
                    {position}
                  </LocationPickers>
                </Tooltip>
              )
            })}
          </PickersBoxRow>
        ))}
      </PickersBoxRowContainer>
    </div>
  )
}
